import create from 'zustand'
import { Store } from '../../api/store'
import { auth } from '../auth'
import { fetchRequest } from '../auth/fetch-request'
import { FormApi, formApi } from '../form'
import { WorksheetResult } from '../worksheet-service'

export type Annotation = {
  start: number
  end: number
  text: string
  tag: string
  color: string
}

export type DocumentFeedback = {
  actualSectionName: string
  bundleId: string
  coderId: string
  coderNote: string
  correctSectionName: string
  text: string
  start: number
  end: number
  row: number
  column: number
}

export function annotationToFeedBack(
  { text, tag }: Partial<Annotation>,
  a: Partial<DocumentFeedback>,
): DocumentFeedback {
  return { ...{ correctSectionName: tag }, ...a, text } as DocumentFeedback
}

const defaultAnnotation = { color: '#ffee00' }

function startEnd(str: string, sub: string): [number, number] | undefined {
  const i = str.indexOf(sub)
  if (i === -1) {
    return undefined
  }
  return [i, i + sub.length]
}

function getTag(coderNote: string, sectionName: string) {
  return coderNote?.length ? sectionName + ' - ' + coderNote : sectionName
}

export function feedBackToAnnotation(
  content: string,
  { text, actualSectionName, correctSectionName, coderNote }: DocumentFeedback | undefined,
): Annotation {
  if (text.indexOf('COUGH') > -1) {
    console.info('feedBackToAnnotation', text)
    //// debugger;
  }
  const i = startEnd(content, text)

  if (!i) {
    return undefined
  }
  const [start, end] = i
  return {
    ...defaultAnnotation,
    ...{ tag: getTag(coderNote, correctSectionName || actualSectionName) },
    text,
    start,
    end,
  } as Annotation
}

const service = 'superchartfeedback'

export const getSectionsListRequest = fetchRequest<void, WorksheetResult<string[]>>({
  method: 'post',
  notFoundResult: () => defaultSections,
  body: () => ({
    service,
    action: 'getsectionslist',
    data: {},
  }),
})

export const getSuperchartFeedbacksRequest = fetchRequest<{ bundleId: string }, WorksheetResult<DocumentFeedback[]>>({
  method: 'post',
  body: (data) => ({
    service,
    action: 'getsuperchartfeedbacks',
    data,
  }),
})

type FeedbackResult = {
  _id: string
  superchartFeedback: DocumentFeedback[]
}

export const saveSuperchartFeedbackRequest = fetchRequest<DocumentFeedback, WorksheetResult<FeedbackResult>>({
  method: 'post',
  disableBodyEdit: true,
  body: (data: DocumentFeedback) => ({
    service,
    action: 'savesuperchartfeedback',
    data: {
      ...{
        coderId: auth?.signedIn?.principalId || auth?.signedIn?.userId,
      },
      ...data,
    },
  }),
})

const defaultSections = [
  'Admin',
  'Allergies',
  'Chief Complaint',
  'Data',
  'Diagnosis/Impression',
  'Discharge Instructions',
  'Disposition',
  'Family History',
  'Follow-up',
  'History of Present Illness',
  'Medical Decision Making',
  'Medications',
  'Other',
  'Past Medical History',
  'Past Surgical History',
  'Physical Exam',
  'Prescription Medications',
  'Procedures',
  'Review of Systems',
  'Signature',
  'Social History',
]

type AnnotationData = {
  text: string
  selected: Annotation
  annotations: Annotation[]
}

export type AnnotationApi = {
  data: Store<AnnotationData>
  sectionForm: FormApi
  save: () => void
  onSelectionChange: (a: Annotation[]) => void
  onCancelSelection: () => void
}

type AnnotationApiProps = {
  text: string
  section: string
  bundleId: string
  feedback?: DocumentFeedback[]
  sections?: string[]
  onSave?: (a: DocumentFeedback) => void
  row?: number
  column?: number
}

function strEscape(str: string) {
  if (!str) {
    return undefined
  }
  let s = JSON.stringify(str)
  s = s.substring(1)
  s = s.substring(0, s.length - 1)
  return s
}

export function annotationApi(a?: AnnotationApiProps): AnnotationApi {
  const { bundleId, feedback, sections, section, onSave, text: content, row, column } = a || {}
  const text = a.text ? a.text.split(/\\n/g).join('\n') : ''
  const modifiedContent = content.split(/\\n/g).join('\n')

  const data = create<AnnotationData>(() => ({
    text: text || '',
    annotations: [],
    selected: null,
  }))

  if (text && feedback?.length) {
    const annotations = feedback
      .filter(
        (a) =>
          a.actualSectionName === section &&
          (modifiedContent.indexOf(a.text.split(/\\n/g).join('\n')) > -1 ||
            modifiedContent.indexOf(a.text.split(/\\r/).join('\r')) > -1 ||
            modifiedContent.indexOf(a.text.split(/\\r\\n/).join('\r\n')) > -1) &&
          ((!row && !column) || (a.row === row && a.column === column)),
      )
      .map((a) => ({
        ...defaultAnnotation,
        ...{
          tag: getTag(a.coderNote, a.correctSectionName || a.actualSectionName),
        },
        text,
        start: a?.start ? a.start : startEnd(text, a.text) ? startEnd(text, a.text)[0] : undefined,
        end: a?.end ? a.end : startEnd(text, a.text) ? startEnd(text, a.text)[1] : undefined,
        row: (a?.row as number) || undefined,
        column: (a?.column as number) || undefined,
      }))

    if (annotations?.length) {
      data.setState({ annotations })
    }
  }

  function save() {
    if (bundleId) {
      const selected = data.getState().selected

      if (selected && auth.signedIn) {
        let note = coderNote.value.getState().value
        note = note?.length ? strEscape(note) : ''

        const feedback = annotationToFeedBack(selected, {
          bundleId,
          actualSectionName: section,
          correctSectionName: correctSectionName.value.getState().value,
          coderNote: note,
          start: selected.start,
          end: selected.end,
          row: a.row,
          column: a.column,
        })

        feedback.text = strEscape(feedback.text)

        saveSuperchartFeedbackRequest(feedback).then(() => {
          if (onSave) {
            onSave(feedback)
          }
        })

        const tag = getTag(note, feedback.correctSectionName)
        const annotations = data.getState().annotations
        annotations.forEach((a, i) => {
          if (a.text === selected.text) {
            annotations[i] = { ...a, ...{ tag } }
          }
        })

        selected.tag = feedback.correctSectionName
        data.setState({ annotations: annotations.slice() })
      }
    }
  }

  const sectionForm = formApi({
    values: {
      correctSectionName: null,
      coderNote: null,
    },
    fields: {
      correctSectionName: {
        required: true,
        label: 'Correct Section Name',
        listLabel: (v) => v,
        onChange: (v) => {
          if (v) {
            const selected = data.getState().selected
            if (selected) {
              selected.tag = v
              data.setState({ selected })
            }
          }
        },
        list: sections || defaultSections,
      },
      coderNote: {
        label: 'Comments',
        multiline: true,
        rows: 3,
        onEnter: () => sectionForm.submit(),
      },
    },
  })

  const { correctSectionName, coderNote } = sectionForm.controls

  const onRemoved = (_a: Annotation) => {
    data.setState({ selected: null })
  }

  const onAdded = (selected: Annotation) => {
    data.setState({ selected })
  }
  const onSelectionChange = (change: Annotation[]) => {
    const previous = data.getState().annotations || []
    const isRemoved = change.length < previous.length

    if (isRemoved) {
      onRemoved(previous[previous.length - 1])
    } else {
      onAdded(change[change.length - 1])
    }
    data.setState({ annotations: change })
  }

  function onCancelSelection() {
    const value = data.getState().annotations
    if (value.length) {
      value.pop()
      data.setState({ annotations: value.slice() })
    }
  }

  return {
    data,
    sectionForm,
    save,
    onSelectionChange,
    onCancelSelection,
  }
}

export function initAnnotations(
  text: string,
  section: string,
  bundleId: string,
  feedback: DocumentFeedback[],
  onSave: () => void,
  row?: number,
  column?: number,
): AnnotationApi {
  const api = annotationApi({
    text,
    section,
    bundleId,
    feedback,
    onSave,
    row,
    column,
  })
  return api
}
